import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  Divider,
  IconButton,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tabs,
  Tab,
  Avatar,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  ImageList,
  ImageListItem,
  Tooltip,
} from "@mui/material";
import {
  Hotel as HotelIcon,
  EditOutlined as EditOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  Add as AddIcon,
  MonetizationOn as MonetizationOnIcon,
  MeetingRoom as MeetingRoomIcon,
  LocationOn as LocationOnIcon,
  Wifi as WifiIcon,
  Pool as PoolIcon,
  FreeBreakfast as FreeBreakfastIcon,
  LocalParking as LocalParkingIcon,
  Accessible as AccessibleIcon,
  AcUnit as AcUnitIcon,
  Room as RoomIcon,
  ImageOutlined as ImageOutlinedIcon,
  Save as SaveIcon,
  BedroomParent as BedroomParentIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

const Property = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const fileInputRef = useRef(null);
  
  // Property details - moved here before it's used in useState
  const property = {
    name: "Grand Hotel & Resort",
    description: "A luxury hotel located in the heart of downtown, offering world-class amenities, fine dining, and exceptional service.",
    address: "123 Main Street, Metropolis",
    phone: "+1 (555) 123-4567",
    email: "info@grandhotelresort.com",
    website: "www.grandhotelresort.com",
    amenities: [
      { name: "Free Wi-Fi", icon: <WifiIcon />, enabled: true },
      { name: "Swimming Pool", icon: <PoolIcon />, enabled: true },
      { name: "Breakfast", icon: <FreeBreakfastIcon />, enabled: true },
      { name: "Parking", icon: <LocalParkingIcon />, enabled: true },
      { name: "Accessibility", icon: <AccessibleIcon />, enabled: true },
      { name: "Air Conditioning", icon: <AcUnitIcon />, enabled: true },
    ]
  };
  
  const [tabValue, setTabValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [propertyImages, setPropertyImages] = useState([
    { id: 1, src: "https://source.unsplash.com/random/300x200/?hotel", name: "exterior.jpg" },
    { id: 2, src: "https://source.unsplash.com/random/300x200/?hotel-lobby", name: "lobby.jpg" }
  ]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [propertyData, setPropertyData] = useState({ ...property });

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleOpenDialog = (room = null) => {
    setEditingRoom(room);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRoom(null);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPropertyData({
      ...propertyData,
      [name]: value
    });
  };
  
  const handleSavePropertyDetails = () => {
    setSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setSuccessMessage("Property details updated successfully!");
    }, 1000);
  };
  
  const handleFileChange = (e) => {
    if (e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    if (!file.type.match('image.*')) {
      setSuccessMessage("Please select an image file");
      return;
    }
    
    setUploadingImage(true);
    
    // Create a preview
    const reader = new FileReader();
    reader.onloadend = () => {
      const newImage = {
        id: Date.now(),
        src: reader.result,
        name: file.name
      };
      
      setPropertyImages([...propertyImages, newImage]);
      setUploadingImage(false);
      setSuccessMessage("Image uploaded successfully");
    };
    
    reader.readAsDataURL(file);
  };
  
  const openFileDialog = () => {
    fileInputRef.current.click();
  };
  
  const handleDeleteImage = (image) => {
    setImageToDelete(image);
    setDeleteConfirmOpen(true);
  };
  
  const confirmDeleteImage = () => {
    if (!imageToDelete) return;
    setPropertyImages(propertyImages.filter(img => img.id !== imageToDelete.id));
    setDeleteConfirmOpen(false);
    setImageToDelete(null);
    setSuccessMessage("Image deleted successfully");
  };
  
  const handleAmenityToggle = (index) => {
    const updatedAmenities = [...propertyData.amenities];
    updatedAmenities[index] = {
      ...updatedAmenities[index],
      enabled: !updatedAmenities[index].enabled
    };
    
    setPropertyData({
      ...propertyData,
      amenities: updatedAmenities
    });
  };

  // Room types
  const roomTypes = [
    {
      id: 1,
      name: "Standard Room",
      capacity: "2 Adults",
      price: 100,
      availability: 14,
      description: "Comfortable room with a queen-sized bed and basic amenities.",
      image: "standard-room.jpg",
      amenities: ["Free Wi-Fi", "TV", "En-suite bathroom"],
    },
    {
      id: 2,
      name: "Deluxe Room",
      capacity: "2 Adults, 1 Child",
      price: 150,
      availability: 8,
      description: "Spacious room with a king-sized bed and premium amenities.",
      image: "deluxe-room.jpg",
      amenities: ["Free Wi-Fi", "TV", "En-suite bathroom", "Mini bar", "Desk"],
    },
    {
      id: 3,
      name: "Suite",
      capacity: "4 Adults",
      price: 250,
      availability: 3,
      description: "Luxurious suite with separate living area and stunning views.",
      image: "suite.jpg",
      amenities: ["Free Wi-Fi", "TV", "En-suite bathroom", "Kitchenette", "Sofa bed", "Balcony"],
    },
  ];

  // Animation variants
  const containerAnimation = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary"
          sx={{ display: "flex", alignItems: "center" }}>
          <HotelIcon sx={{ mr: 1 }} /> Property Management
        </Typography>
      </motion.div>

      <Box sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab icon={<HotelIcon />} iconPosition="start" label="Property Details" />
          <Tab icon={<MeetingRoomIcon />} iconPosition="start" label="Room Types" />
          <Tab icon={<MonetizationOnIcon />} iconPosition="start" label="Pricing & Availability" />
        </Tabs>
      </Box>

      {/* Property Details Tab */}
      <Box sx={{ display: tabValue === 0 ? "block" : "none" }}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerAnimation}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <motion.div variants={itemAnimation} transition={{ duration: 0.3 }}>
                <Card elevation={2}>
                  <CardHeader 
                    title="Property Details" 
                    action={
                      <IconButton color="primary">
                        <EditOutlinedIcon />
                      </IconButton>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Property Name"
                          name="name"
                          defaultValue={propertyData.name}
                          onChange={handleInputChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <HotelIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Description"
                          name="description"
                          multiline
                          rows={4}
                          defaultValue={propertyData.description}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Address"
                          name="address"
                          defaultValue={propertyData.address}
                          onChange={handleInputChange}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationOnIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          name="phone"
                          defaultValue={propertyData.phone}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          defaultValue={propertyData.email}
                          onChange={handleInputChange}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Website"
                          name="website"
                          defaultValue={propertyData.website}
                          onChange={handleInputChange}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
                        sx={{ borderRadius: 2 }}
                        onClick={handleSavePropertyDetails}
                        disabled={submitting}
                      >
                        {submitting ? "Saving..." : "Save Changes"}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <motion.div variants={itemAnimation} transition={{ duration: 0.3, delay: 0.1 }}>
                <Card elevation={2}>
                  <CardHeader title="Amenities" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={1}>
                      {propertyData.amenities.map((amenity, index) => (
                        <Grid item xs={6} key={index}>
                          <Card 
                            variant="outlined" 
                            sx={{ 
                              p: 1, 
                              display: 'flex', 
                              alignItems: 'center',
                              transition: 'all 0.3s',
                              '&:hover': {
                                backgroundColor: 'action.hover',
                                transform: 'translateY(-3px)',
                              },
                              bgcolor: amenity.enabled ? 'transparent' : 'action.disabledBackground',
                            }}
                          >
                            <Box 
                              sx={{ 
                                mr: 1,
                                color: amenity.enabled ? 'primary.main' : 'text.disabled',
                              }}
                            >
                              {amenity.icon}
                            </Box>
                            <Typography 
                              variant="body2"
                              sx={{
                                color: amenity.enabled ? 'text.primary' : 'text.disabled',
                              }}
                            >
                              {amenity.name}
                            </Typography>
                            <Box sx={{ flexGrow: 1 }}></Box>
                            <Switch 
                              size="small" 
                              checked={amenity.enabled} 
                              onChange={() => handleAmenityToggle(index)}
                            />
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                    <Button 
                      fullWidth 
                      variant="outlined" 
                      startIcon={<AddIcon />} 
                      sx={{ mt: 2 }}
                    >
                      Add Amenity
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div variants={itemAnimation} transition={{ duration: 0.3, delay: 0.2 }}>
                <Card elevation={2} sx={{ mt: 3 }}>
                  <CardHeader 
                    title="Property Images" 
                    action={
                      <Tooltip title="Upload new image">
                        <IconButton 
                          color="primary"
                          onClick={openFileDialog}
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? <CircularProgress size={24} /> : <CloudUploadIcon />}
                        </IconButton>
                      </Tooltip>
                    }
                  />
                  <Divider />
                  <CardContent>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                      onChange={handleFileChange}
                    />
                    
                    {propertyImages.length > 0 ? (
                      <ImageList cols={2} rowHeight={120} gap={8}>
                        {propertyImages.map((image) => (
                          <ImageListItem key={image.id}>
                            <img
                              src={image.src}
                              alt={image.name}
                              loading="lazy"
                              style={{ borderRadius: 4, height: '100%', objectFit: 'cover' }}
                            />
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.8)' },
                              }}
                              onClick={() => handleDeleteImage(image)}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </ImageListItem>
                        ))}
                      </ImageList>
                    ) : (
                      <Box 
                        sx={{ 
                          p: 3, 
                          border: '2px dashed',
                          borderColor: 'divider',
                          borderRadius: 1,
                          textAlign: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={openFileDialog}
                      >
                        <ImageOutlinedIcon sx={{ fontSize: 40, color: 'action.active', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Drag & drop images here or click to upload
                        </Typography>
                        <Button variant="outlined" size="small">
                          Browse Files
                        </Button>
                      </Box>
                    )}
                    
                    {propertyImages.length > 0 && (
                      <Box 
                        sx={{ 
                          mt: 2,
                          p: 2,
                          border: '2px dashed',
                          borderColor: 'divider',
                          borderRadius: 1, 
                          textAlign: 'center',
                          cursor: 'pointer'
                        }}
                        onClick={openFileDialog}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Click to add more images
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Recommended: High-resolution images in landscape orientation.
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </motion.div>
      </Box>

      {/* Room Types Tab */}
      <Box sx={{ display: tabValue === 1 ? "block" : "none" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">Room Types</Typography>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add New Room Type
            </Button>
          </Box>
          
          <Grid container spacing={3}>
            {roomTypes.map((room, index) => (
              <Grid item xs={12} sm={6} md={4} key={room.id}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  whileHover={{ translateY: -5 }}
                >
                  <Card 
                    elevation={2} 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        height: 140,
                        bgcolor: 'primary.light',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <BedroomParentIcon sx={{ fontSize: 80, color: 'white', opacity: 0.5 }} />
                      <Typography 
                        variant="h5" 
                        sx={{ 
                          position: 'absolute', 
                          color: 'white',
                          fontWeight: 'bold',
                          textShadow: '1px 1px 3px rgba(0,0,0,0.6)'
                        }}
                      >
                        {room.name}
                      </Typography>
                    </Box>
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Price per night
                        </Typography>
                        <Typography variant="h5" color="primary" fontWeight="bold">
                          ${room.price}
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ my: 1 }} />
                      
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip 
                            size="small" 
                            label={`${room.availability} Rooms`} 
                            color={room.availability > 5 ? "success" : room.availability > 0 ? "warning" : "error"} 
                            sx={{ mr: 1 }}
                          />
                          <Typography variant="body2">{room.capacity}</Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary">
                          {room.description}
                        </Typography>
                        
                        <Box sx={{ mt: 1 }}>
                          {room.amenities.map((amenity, idx) => (
                            <Chip 
                              key={idx} 
                              label={amenity} 
                              size="small" 
                              sx={{ mr: 0.5, mb: 0.5 }} 
                              variant="outlined" 
                            />
                          ))}
                        </Box>
                      </Stack>
                    </CardContent>
                    
                    <Divider />
                    <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
                      <Button 
                        size="small" 
                        startIcon={<EditOutlinedIcon />} 
                        onClick={() => handleOpenDialog(room)}
                      >
                        Edit
                      </Button>
                      <Button 
                        size="small" 
                        color="error" 
                        startIcon={<DeleteOutlineIcon />}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Box>

      {/* Pricing & Availability Tab */}
      <Box sx={{ display: tabValue === 2 ? "block" : "none" }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardHeader title="Base Pricing" />
                <Divider />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Set your base pricing for each room type. Seasonal adjustments can be made below.
                  </Typography>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Room Type</TableCell>
                          <TableCell align="right">Base Price ($)</TableCell>
                          <TableCell align="right">Weekend Rate ($)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roomTypes.map((room) => (
                          <TableRow key={room.id}>
                            <TableCell>{room.name}</TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                defaultValue={room.price}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                sx={{ width: '120px' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                defaultValue={Math.round(room.price * 1.25)}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                sx={{ width: '120px' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Discounts</Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Early Bird Discount"
                          type="number"
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          defaultValue={10}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Last Minute Discount"
                          type="number"
                          size="small"
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          defaultValue={15}
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                    <Button variant="contained" color="primary">
                      Update Pricing
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardHeader title="Availability Management" />
                <Divider />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Set total room inventory and minimum stay requirements.
                  </Typography>
                  
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Room Type</TableCell>
                          <TableCell align="right">Total Rooms</TableCell>
                          <TableCell align="right">Min. Stay (nights)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roomTypes.map((room) => (
                          <TableRow key={room.id}>
                            <TableCell>{room.name}</TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                defaultValue={room.availability}
                                sx={{ width: '80px' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                defaultValue={1}
                                sx={{ width: '80px' }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Seasonal Closures</Typography>
                    <FormControlLabel 
                      control={<Switch color="primary" />} 
                      label="Close hotel during winter season" 
                    />
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          label="From"
                          type="date"
                          size="small"
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="To"
                          type="date"
                          size="small"
                          fullWidth
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                    <Button variant="contained" color="primary">
                      Update Availability
                    </Button>
                  </Box>
                </CardContent>
              </Card>
              
              <Card elevation={2} sx={{ mt: 3 }}>
                <CardHeader title="Channel Management" />
                <Divider />
                <CardContent>
                  <Typography variant="body2" paragraph>
                    Control availability across different booking channels
                  </Typography>
                  
                  {["Booking.com", "Expedia", "Airbnb", "Direct Website"].map((channel, idx) => (
                    <FormControlLabel
                      key={idx}
                      control={
                        <Switch defaultChecked={idx !== 3} color="primary" />
                      }
                      label={channel}
                      sx={{ display: 'block', mb: 1 }}
                    />
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      </Box>

      {/* Add/Edit Room Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{editingRoom ? "Edit Room Type" : "Add New Room Type"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Room Name"
                fullWidth
                defaultValue={editingRoom?.name || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Capacity"
                fullWidth
                defaultValue={editingRoom?.capacity || ""}
                placeholder="e.g. 2 Adults, 1 Child"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Base Price"
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                defaultValue={editingRoom?.price || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Number of Rooms"
                fullWidth
                type="number"
                defaultValue={editingRoom?.availability || ""}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                defaultValue={editingRoom?.description || ""}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Amenities
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {["Free Wi-Fi", "TV", "Air Conditioning", "Mini Bar", "Safe", "Desk", "Bathtub", "Shower", "Balcony", "Sea View"].map((amenity) => (
                  <Chip
                    key={amenity}
                    label={amenity}
                    onClick={() => {}}
                    color={editingRoom?.amenities?.includes(amenity) ? "primary" : "default"}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box 
                sx={{ 
                  p: 3, 
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  textAlign: 'center',
                  mt: 2
                }}
              >
                <ImageOutlinedIcon sx={{ fontSize: 40, color: 'action.active', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Upload room images
                </Typography>
                <Button variant="outlined" size="small">
                  Browse Files
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" color="primary">
            {editingRoom ? "Save Changes" : "Add Room"}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Image Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <DialogContent>Are you sure you want to delete this image? This action cannot be undone.</DialogContent>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteImage} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Property;